import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetWriting,
  useUpdateWriting,
} from "@/api/writing/writing";
import {
  appendNoDuplicateRecordNumberRefine,
  writingSchema,
} from "@/api/writing/writing-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldWritingDescription } from "@/lib/computeFields";
import { Writing } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(writingSchema.passthrough());

const EditWriting = ({ writing }: { writing: Writing }) => {
  const { data: attachments } = useGetAttachmentsByDocId(writing.id);

  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: writing,
  });

  const writingUpdater = useUpdateWriting();

  const { t } = useTranslation();

  const handleLegendeRedigee = (data: Writing) => {
    return computeFieldWritingDescription(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Writing>({
    statusField: "ecritsPublicationStatus",
    resourceName: "writing",
    relativePosition: {
      currentId: writing.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  const [, setSearchParams] = useSearchParams();

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            writingUpdater.mutate(
              {
                ...values,
                ...(status && { ecritsPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("writingSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ecritsDescriptionNumeroEnregistrement != undefined)
              setSearchParams({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({ queryKey: ["writing", writing.id] });
        queryClient.invalidateQueries({ queryKey: ["writings"] });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: writing.id }],
        });
      }}
      relativePosition={relativePosition}
      publicationStatus={writing.ecritsPublicationStatus}
      title={writing.ecritsDescriptionTitre}
      reference={writing.ecritsDescriptionNumeroEnregistrement}
      id={writing.id}
      type="writing"
      picturesData={
        attachments && {
          pictures: attachments,
          type: "writing",
          size: "mediumPublicUrl",
        }
      }
      form={form}
    >
      <AF.AbstractForm
        form={form}
        tabs={[
          { value: "description", label: t("writingDescription") },
          { value: "retranscription", label: t("retranscription") },
          { value: "sources", label: t("sources") },
          { value: "files", label: t("fichiers") },
          { value: "links", label: t("linkedEntries") },
        ]}
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row>
              <AF.Text
                name="ecritsDescriptionNumeroEnregistrement"
                className="w-64"
              />
              <AF.Checkbox name="ecritsDescriptionCopies" align={true} />
            </AF.Row>
            <ModifiableList
              name="ecritsDescriptionType"
              listKey="liste-types-document"
            />
            <AF.Row>
              <AF.Text name="ecritsDescriptionClassement" />
              <AF.Text name="ecritsDescriptionRangement" />
            </AF.Row>
            <ModifiableList
              name="ecritsDescriptionLocalisation"
              listKey="liste-bibliotheques-archives"
            />
            <AF.Text
              className="w-16"
              name="ecritsDescriptionNombreExemplairesArchives"
            />
            <AF.Text name="ecritsDescriptionTitre" />
            <AF.Row>
              <AF.Text name="ecritsDescriptionDate" />
              <ModifiableList
                name="ecritsDescriptionTheme"
                listKey="liste-themes"
              />
            </AF.Row>
            <TwoDimensionalInput parentName="ecritsDescriptionDimensions" />
            <ModifiableList
              name="ecritsDescriptionTechnique"
              listKey="liste-techniques-document"
            />
            <AF.Text name="ecritsDescriptionLieuDeCreation" />
            <ModifiableList
              name="ecritsDescriptionPhotographe"
              listKey="liste-photographes"
            />
            <ModifiableList
              name="ecritsDescriptionCopyright"
              listKey="liste-credits"
            />
            <AF.Text className="w-16" name="ecritsDescriptionNombreFeuillet" />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="ecritsDescriptionLegendeRedigee"
              computeAction={handleLegendeRedigee}
            />
            <AF.RichText name="ecritsDescriptionNotes" />
            <AF.Row>
              <AF.Checkbox name="ecritsFichierPhotoBasseDefinitionUniquement" />
              <AF.Checkbox name="ecritsFichierPhotoScanAFaire" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="ecritsFichierPhotoOkWeb" />
              <AF.Checkbox name="ecritsFichierPhotoHdOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="ecritsValidationPublicationNumerisee" />
              <AF.Checkbox name="ecritsValidationPdfEtOcrOk" />
            </AF.Row>
            <Separator />
            <AF.RadioGroup
              resettable={true}
              items={[
                { value: "ecritsAcquisitionDocumentAAcquerir" },
                {
                  value: "ecritsAcquisitionDocumentAAcquis",
                },
              ]}
            />
            <AF.Row>
              <AF.Text name="ecritsAcquisitionDateAcquisition" />
              <AF.Text name="ecritsAcquisitionVendeur" />
            </AF.Row>
            <AF.RichText name="ecritsAcquisitionNotes" />
            <AF.Text name="ecritsMouvementsDepot" />
            <AF.RichText name="ecritsMouvementsNotesDepot" />
            <AF.LegacySelectCurrency name="ecritsMouvementsValeurAssurance" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="retranscription">
          <AF.RichText name="ecritsDescriptionRetranscription" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab value="files" headerExtras={<AddFileModal id={writing.id} />}>
          <AF.OthersFilesTable name="diversFiles" docId={writing.id} />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={writing.id}
            type="writing"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={writing}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditWritingPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetWriting(id);

  return (
    <>
      {data && <EditWriting key={data.id} writing={data} />}
      {!data && !isLoading && <h2 className="p-4">ecritNExistePasLabel </h2>}
    </>
  );
};
